import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@/components/Alert';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'components/i18n/Translator';

import { useQuery } from 'hooks/query';

import { ChainlitContext, useAuth } from 'client-types/*';

export const LoginError = new Error(
  'Error logging in. Please try again later.'
);

export default function Login() {
  const query = useQuery();
  const { data: config, user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const providers = config?.oauthProviders || [];

  const handleCookieAuth = (json: any): void => {
    if (json?.success != true) throw LoginError;

    // Validate login cookie and get user data.
    setUserFromAPI();
  };

  const handleAuth = async (
    jsonPromise: Promise<any>,
    redirectURL?: string
  ) => {
    try {
      const json = await jsonPromise;

      handleCookieAuth(json);

      if (redirectURL) {
        navigate(redirectURL);
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleHeaderAuth = async () => {
    const jsonPromise = apiClient.headerAuth();

    await handleAuth(jsonPromise, '/');
  };

  useEffect(() => {
    setError(query.get('error') || '');
  }, [query]);

  useEffect(() => {
    if (!config) {
      return;
    }
    if (!config.requireLogin) {
      navigate('/');
    }
    if (config.headerAuth && !user) {
      handleHeaderAuth();
    }
    if (user) {
      navigate('/');
    }
  }, [config, user]);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-white p-6">
      {error && (
        <Alert variant="error">
          {t([
            `auth.login.errors.${error.toLowerCase()}`,
            `auth.login.errors.default`
          ])}
        </Alert>
      )}

      <div className="flex flex-col-reverse items-center justify-center gap-10 sm:flex-row sm:gap-24">
        <Button
          type="button"
          variant="outline"
          disabled={!providers.length}
          className="border-black bg-white text-black hover:bg-white hover:text-black sm:h-14 sm:px-10 sm:text-lg"
          onClick={() => {
            if (providers.length) {
              window.location.href = apiClient.getOAuthEndpoint(providers[0]);
            }
          }}
        >
          Access the AI Assistant
        </Button>

        <img
          src={
            config?.ui?.login_page_image ||
            apiClient.buildEndpoint('/favicon')
          }
          alt="Logo"
          className="max-h-[35vh] w-auto max-w-[80vw] object-contain sm:max-h-[35vh] sm:max-w-[30vw]"
        />
      </div>
    </div>
  );
}
